import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  AuthorizePusherChannelAPI,
  GetHousekeepingNotificationsAPI,
  MarkHousekeepingNotificationsReadAPI,
} from "api/pusher";

/* =========================================================
   THUNKS
========================================================= */

export const getHousekeepingNotifications = createAsyncThunk(
  "pusher/getHousekeepingNotifications",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const response = await GetHousekeepingNotificationsAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch notifications"
      );
    }
  }
);

export const markHousekeepingNotificationsRead = createAsyncThunk(
  "pusher/markHousekeepingNotificationsRead",
  async (ids, { rejectWithValue }) => {
    try {
      await MarkHousekeepingNotificationsReadAPI(ids);

      return {
        ids: ids || [],
        markAll: !ids?.length,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to mark notifications as read"
      );
    }
  }
);

export const authorizePusherChannel = createAsyncThunk(
  "pusher/authorizePusherChannel",
  async ({ socket_id, channel_name }, { rejectWithValue }) => {
    try {
      const response = await AuthorizePusherChannelAPI({
        socket_id,
        channel_name,
      });

      return response?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to authorize Pusher channel"
      );
    }
  }
);

/* =========================================================
   INITIAL STATE
========================================================= */

const initialState = {
  notifications: [],
  roomRefreshTrigger: 0,

  notificationsPage: 1,
  notificationsPageSize: 20,
  notificationsTotal: 0,

  notificationsLoading: false,
  notificationsError: null,

  unreadCount: 0,

  markReadLoading: false,
  markReadError: null,
  markReadSuccess: false,

  pusherAuthLoading: false,
  pusherAuthError: null,

  connected: false,

  subscribedHotelChannel: null,
  subscribedUserChannel: null,
};

/* =========================================================
   SLICE
========================================================= */

const pusherSlice = createSlice({
  name: "pusher",

  initialState,

  reducers: {
    resetNotificationsState: (state) => {
      state.notifications = [];
      state.notificationsPage = 1;
      state.notificationsPageSize = 20;
      state.notificationsTotal = 0;
      state.notificationsLoading = false;
      state.notificationsError = null;
      state.unreadCount = 0;
    },

    resetMarkReadState: (state) => {
      state.markReadLoading = false;
      state.markReadError = null;
      state.markReadSuccess = false;
    },

    triggerHousekeepingRoomsRefresh: (state) => {
      state.roomRefreshTrigger += 1;
    },

    setPusherConnected: (state, action) => {
      state.connected = action.payload;
    },

    setSubscribedHotelChannel: (state, action) => {
      state.subscribedHotelChannel = action.payload;
    },

    setSubscribedUserChannel: (state, action) => {
      state.subscribedUserChannel = action.payload;
    },

    addNotification: (state, action) => {
      const notification = action.payload;

      const notificationId = notification?._id || notification?.id;

      if (!notificationId) {
        console.log("❌ Redux ignored notification: missing ID");

        return;
      }

      const alreadyExists = state.notifications.some(
        (item) => (item?._id || item?.id) === notificationId
      );

      state.notifications.unshift({
        ...notification,
        _id: notificationId,
        isRead: notification.isRead ?? false,
      });

      state.unreadCount += 1;
    },

    markNotificationReadLocally: (state, action) => {
      const notificationId = action.payload;

      const notification = state.notifications.find(
        (item) => (item?._id || item?.id) === notificationId
      );

      if (notification && !notification.isRead) {
        notification.isRead = true;

        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    clearPusherSubscriptionState: (state) => {
      state.connected = false;
      state.subscribedHotelChannel = null;
      state.subscribedUserChannel = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // ===========================
      // NOTIFICATIONS
      // ===========================

      .addCase(getHousekeepingNotifications.pending, (state) => {
        state.notificationsLoading = true;
        state.notificationsError = null;
      })

      .addCase(getHousekeepingNotifications.fulfilled, (state, action) => {
        state.notificationsLoading = false;

        const items = action.payload?.items || [];

        state.notifications = items;

        state.notificationsPage = action.payload?.page || 1;

        state.notificationsPageSize = action.payload?.pageSize || 20;

        state.notificationsTotal = action.payload?.total || items.length;

        state.unreadCount = items.filter((item) => !item.isRead).length;
      })

      .addCase(getHousekeepingNotifications.rejected, (state, action) => {
        state.notificationsLoading = false;
        state.notificationsError = action.payload;
      })

      // ===========================
      // MARK READ
      // ===========================

      .addCase(markHousekeepingNotificationsRead.pending, (state) => {
        state.markReadLoading = true;
        state.markReadError = null;
        state.markReadSuccess = false;
      })

      .addCase(markHousekeepingNotificationsRead.fulfilled, (state, action) => {
        state.markReadLoading = false;
        state.markReadSuccess = true;

        const { ids, markAll } = action.payload;

        if (markAll) {
          state.notifications.forEach((notification) => {
            notification.isRead = true;
          });

          state.unreadCount = 0;

          return;
        }

        state.notifications.forEach((notification) => {
          if (ids.includes(notification._id)) {
            notification.isRead = true;
          }
        });

        state.unreadCount = state.notifications.filter(
          (notification) => !notification.isRead
        ).length;
      })

      .addCase(markHousekeepingNotificationsRead.rejected, (state, action) => {
        state.markReadLoading = false;
        state.markReadError = action.payload;
      })

      // ===========================
      // PUSHER AUTH
      // ===========================

      .addCase(authorizePusherChannel.pending, (state) => {
        state.pusherAuthLoading = true;
        state.pusherAuthError = null;
      })

      .addCase(authorizePusherChannel.fulfilled, (state) => {
        state.pusherAuthLoading = false;
      })

      .addCase(authorizePusherChannel.rejected, (state, action) => {
        state.pusherAuthLoading = false;
        state.pusherAuthError = action.payload;
      });
  },
});

export const {
  resetNotificationsState,
  resetMarkReadState,
  triggerHousekeepingRoomsRefresh,
  setPusherConnected,
  setSubscribedHotelChannel,
  setSubscribedUserChannel,
  addNotification,
  markNotificationReadLocally,
  clearPusherSubscriptionState,
} = pusherSlice.actions;

export default pusherSlice.reducer;
